import { useEffect, useMemo } from "react";
import { useResourcePersonDashboard } from "../store/resourcePerson/resourcePersonStore";

/**
 * Resource-person notifications.
 *
 * There is no `/api/resource-person/notifications` endpoint in the resource
 * person API. The dashboard aggregate does expose the account's recent
 * `activity`, so the bell shows that real activity instead of hardcoded demo
 * messages. Each item is derived from `GET /api/resource-person/dashboard`.
 */
const titleCase = (value) =>
  String(value || "")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export function relativeTime(value) {
  if (!value) return "";
  const then = new Date(value).getTime();
  if (Number.isNaN(then)) return "";
  const seconds = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (seconds < 60) return "just now";
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(value).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// The bell is rendered by two navbars at once. Without a guard, a failed
// dashboard request flips `dashboardLoading` back to false, the effect re-runs,
// and it immediately fetches again — an infinite request/re-render loop that
// makes the whole resource-person area feel stuck. Keep one shared cooldown so
// a failure retries at most once a minute and never in a tight loop.
let lastDashboardRequestAt = 0;
const DASHBOARD_RETRY_MS = 60 * 1000;

export default function useResourcePersonNotifications() {
  const { dashboard, dashboardLoading, fetchDashboard } = useResourcePersonDashboard();

  useEffect(() => {
    if (dashboard || dashboardLoading) return;
    if (Date.now() - lastDashboardRequestAt < DASHBOARD_RETRY_MS) return;
    lastDashboardRequestAt = Date.now();
    fetchDashboard();
  }, [dashboard, dashboardLoading, fetchDashboard]);

  const notifications = useMemo(() => {
    const activity = Array.isArray(dashboard?.activity) ? dashboard.activity : [];
    return activity.map((item) => ({
      id: item.id,
      title: titleCase(item.action) || "Account activity",
      description: item.targetType ? titleCase(item.targetType) : "Recent account activity",
      createdAt: item.createdAt,
      time: relativeTime(item.createdAt),
      raw: item,
    }));
  }, [dashboard]);

  return {
    notifications,
    loading: dashboardLoading && !dashboard,
    error: dashboard?.error || null,
  };
}
